import { forwardRef, useImperativeHandle, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useOnClickOutside } from 'usehooks-ts';
import type { ModalAction } from '@/components/common/modal/modal';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import EmojiPanel from '@/components/symbol/emoji/emoji-panel';
import StickerPanel from '@/components/symbol/sticker/sticker-panel';
import cn from '@/utils/merge-class-names';
import CustomStickerPanel from './custom-sticker/custom-sticker-panel';
import { SymbolContext } from './symbol-context';
import SymbolSelector from './symbol-selector';

type Props = {
  isOpen: boolean;
  disabled?: boolean;
  onClose?: () => void;
  onEmoji: (emoji: string) => void;
  onSticker: (url: string) => void;
  className?: string;
};

export type SymbolMenuActions = {
  close: () => void;
};

const SymbolMenu = forwardRef<SymbolMenuActions, Props>(
  ({ className, onEmoji, onSticker, onClose, isOpen }, symbolMenuRef) => {
    const ref = useRef<HTMLDivElement>(null);
    const managementModalRef = useRef<ModalAction>(null);

    const handleClose = () => {
      onClose?.();
    };

    useOnClickOutside(ref, handleClose);
    useImperativeHandle(symbolMenuRef, () => ({ close: handleClose }));

    return (
      <div
        ref={ref}
        className='mobile:relative h-full ring-0 desktop:self-end'>
        {isOpen && (
          <div
            className={cn(
              'flex h-72 w-full flex-col gap-2 rounded-lg bg-neutral-0/90 shadow-lg backdrop-blur desktop:absolute desktop:bottom-full desktop:max-w-[30rem] desktop:right-5 desktop:mb-2 desktop:translate-x-0 desktop:bg-neutral-0 desktop:p-4 desktop:shadow-toast desktop:w-screen desktop:rounded-lg desktop:z-20',
              className,
            )}>
            <SymbolContext.Provider
              value={{
                sendEmoji: onEmoji,
                sendSticker: onSticker,
                toggleManagement: () => managementModalRef.current?.open(),
              }}>
              <ErrorBoundary fallback={<LoadingLayout />}>
                <SymbolSelector />
                <EmojiPanel />
                <StickerPanel />
                <CustomStickerPanel />
              </ErrorBoundary>
            </SymbolContext.Provider>
          </div>
        )}
      </div>
    );
  },
);

SymbolMenu.displayName = 'SymbolMenu';

export default SymbolMenu;
